/**
 * Copyright 2021, IntraLinks, Inc. All rights reserved.
 */

/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { replace } from 'connected-react-router';
import { TFunction } from 'i18next';
import { AxiosError } from 'axios';
import { AppThunk } from '../../app/store';
import Repos from '../../infrastructure/repositories/Repos';
import { IProfile, IProfileProperties } from '../../domain/models/IProfile';
import { IProfileExternalApps } from '../../domain/models/IProfileExternalApps';
import { setIsLoading, showToast } from '../../app/shared/AppSlice/AppSlice';
import { I18nNamespaces, getErrorPath } from '../../app/shared/utils/constants.util';
import { getTranslatedErrorKey, getErrorStatusCode } from '../../app/shared/utils/errors.util';
import { IGetListQueryParams } from '../../infrastructure/repositories/utils/IGetListQueryParams';
import { IProfileApps } from '../../domain/models/IProfileApps';

export interface IProfileState {
  profileDetails: IProfileProperties;
  applications: IProfileApps[];
  externalApps: IProfileExternalApps[];
  profileSetupDetails?: Partial<IProfileProperties>;
}

const initialState: IProfileState = {
  profileDetails: {
    id: '', // DO NOT CHANGE IT, IT WILL BREAK LOA ENRICHMENT FLOW
    externalId: '',
    preferences: {
      locale: '',
      timezone: ''
    },
    name: {
      displayName: '',
      firstName: '',
      lastName: ''
    },
    phoneInfos: [],
    addresses: [],
    identityProviderId: '',
    ssoEnabled: false,
    title: '',
    industry: ''
  },
  applications: [],
  externalApps: [],
  profileSetupDetails: undefined
};

export const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    currentProfile: (state: IProfileState, action: PayloadAction<any>): void => {
      state.profileDetails = action.payload.allProfile;
    },
    setUpProfile: (state: IProfileState, action: PayloadAction<any>): void => {
      state.profileSetupDetails = action.payload.allProfile;
    },
    setApplications: (state: IProfileState, action: PayloadAction<any>): void => {
      state.applications = action.payload;
    },
    updateProfileData: (state: IProfileState, action: PayloadAction<any>): void => {
      state.profileDetails = action.payload.updateProfile;
    },
    setExternalApplications: (state: IProfileState, action: PayloadAction<any>): void => {
      state.externalApps = action.payload;
    }
  }
});

export const {
  setUpProfile, updateProfileData, currentProfile, setApplications, setExternalApplications
} = profileSlice.actions;

export const getProfileForEulaCheck = (fields?: (keyof IProfile)[] | undefined): AppThunk => async (dispatch): Promise<void> => {
  // eslint-disable-next-line no-useless-catch
  try {
    dispatch(setIsLoading(true));
    const allProfile = await Repos.instance.profileRepository.get(undefined, { fields } as IGetListQueryParams<IProfile>);
    if (allProfile?.eulaVersion === 2 && !allProfile?.eulaDeclined) {
      dispatch(replace('/profile'));
    }
    dispatch(setIsLoading(false));
  } catch (error) {
    dispatch(setIsLoading(false));
    throw error;
  }
};
export const getProfileForProfileSetup = (fields?: (keyof IProfile)[] | undefined): AppThunk => async (dispatch): Promise<void> => {
  // eslint-disable-next-line no-useless-catch
  try {
    dispatch(setIsLoading(true));
    const allProfile = await Repos.instance.profileRepository.get(undefined, { fields } as IGetListQueryParams<IProfile>);
    dispatch(setUpProfile({ allProfile }));
    dispatch(setIsLoading(false));
  } catch (error) {
    dispatch(setIsLoading(false));
    throw error;
  }
};
export const getProfile = (): AppThunk => async (dispatch): Promise<void> => {
  try {
    const allProfile = await Repos.instance.profileRepository.get(undefined, undefined);
    dispatch(currentProfile({ allProfile }));
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { _status: statusInfo } = err?.response?.data || {};

    const errorStatusCode = statusInfo
      ? statusInfo?.status : undefined;
    const errorCode = getErrorStatusCode(errorStatusCode) || '500';
    dispatch(replace(getErrorPath(errorCode)));
  }
};

export const getApplications = (): AppThunk => async (dispatch): Promise<void> => {
  try {
    const response = await Repos.instance.profileAppRepository.getAll(undefined, undefined);
    dispatch(setApplications(response.items));
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { _status: statusInfo } = err?.response?.data || {};

    const errorStatusCode = statusInfo
      ? statusInfo?.status : undefined;
    const errorCode = getErrorStatusCode(errorStatusCode) || '500';
    dispatch(replace(getErrorPath(errorCode)));
  }
};
export const profileSetup = (req: IProfile): AppThunk => async (): Promise<void> => {
  req.id = '';
  await Repos.instance.profileRepository.update(req, {});
};
export const getUpdateProfile = (updateProfileObj: IProfile, t: TFunction): AppThunk => async (dispatch): Promise<void> => {
  updateProfileObj.id = '';
  try {
    const updateProfile = await Repos.instance.profileRepository.update(updateProfileObj, {});
    dispatch(updateProfileData({ updateProfile }));
    dispatch(showToast({
      title: t('successMessages.profileInfoUpdated', { ns: I18nNamespaces.PROFILE }),
      timeout: 0 as number,
      kind: 'success',
      subtitle: ''
    }));
  } catch (err) {
    const translatedErrorKey = getTranslatedErrorKey(err as AxiosError);
    dispatch(showToast({
      title: t(`${translatedErrorKey}`, { ns: I18nNamespaces.PROFILE }), timeout: 0 as number, kind: 'error', subtitle: ''
    }));
  }
};

export const getExternalApps = (id: string, t: TFunction): AppThunk => async (dispatch): Promise<void> => {
  try {
    const response = await Repos.instance.profileExternalAppsRepository.getAll(undefined, { id });
    dispatch(setExternalApplications(response.items));
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { _status: statusInfo } = err?.response?.data || {};

    const errorStatusCode = statusInfo
      ? statusInfo?.status : undefined;
    const errorCode = getErrorStatusCode(errorStatusCode) || '500';
    dispatch(showToast({
      title: t('externalApps.messages.getExternalApps.error', { ns: I18nNamespaces.PROFILE }),
      timeout: 0 as number,
      kind: 'error',
      subtitle: ''
    }));
    throw errorCode;
  }
};

export const deleteExternalApp = (id: string, consentId: string): AppThunk => async (): Promise<void> => {
  try {
    await Repos.instance.profileExternalAppsRepository.delete('', { id, consentId });
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { _status: statusInfo } = err?.response?.data || {};

    const errorStatusCode = statusInfo
      ? statusInfo?.status : undefined;
    throw getErrorStatusCode(errorStatusCode) || '500';
  }
};

let inFlightUserId: Promise<string> | undefined;

async function fetchUserId(dispatch: any, getState: any): Promise<string> {
  await dispatch(getProfile());
  const id = getState().profile.profileDetails?.id;
  if (!id) throw new Error('Missing user id');
  return id;
}

export const requireUserId = (): AppThunk<Promise<string>> => (dispatch, getState) => {
  const existing = getState().profile.profileDetails?.id;
  if (existing) return Promise.resolve(existing);

  if (!inFlightUserId) {
    inFlightUserId = fetchUserId(dispatch, getState)
      .finally(() => {
        inFlightUserId = undefined;
      });
  }

  return inFlightUserId;
};

export default profileSlice.reducer;
